import { useMemo, type CSSProperties } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { msg, TovCard } from '@tov-plus/ui';

const seatingErrorMessage = msg('pages.seating_page.guest_seating_load_error');

export function SeatingPage() {
  const access = useGuestAccess();
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const seatingQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.seating(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.seating('pending', 'pending'),
    queryFn: () =>
      guestApi.listSeatingSummary(readyOccasionSlug!, readyGuestToken!),
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });
  const items = seatingQuery.data ?? [];
  const companionNames =
    access.status === 'ready'
      ? access.experience.identitySummary.participantDisplayNames
      : [];
  const seatingAlerts = useMemo(
    () =>
      access.status === 'ready'
        ? access.experience.recentCommunications.filter((item) => {
            const body = item.message?.bodyText.toLowerCase() ?? '';
            const subject = item.message?.subjectText?.toLowerCase() ?? '';
            return (
              body.includes('seat') ||
              body.includes('table') ||
              subject.includes('seat') ||
              subject.includes('table')
            );
          })
        : [],
    [access],
  );
  const tableSummaries = useMemo(() => {
    const coveredCompanions = companionNames.filter((name) => name.trim());
    return items.map((item) => ({
      id: item.assignment.id,
      title: item.table.label,
      eventName: item.event.displayName,
      seatLabel:
        item.assignment.seatLabel ??
        item.assignment.seatNumber?.toString() ??
        null,
      coveredCompanions,
      notes:
        item.table.notes ??
        item.assignment.notes ??
        msg(
          'pages.seating_page.no_published_comfort_or_accessibility_notes_for_this_table_yet',
        ),
    }));
  }, [companionNames, items]);
  const seatingMapLayout = useMemo(() => {
    const xValues = items
      .map((item) => item.table.layoutX ?? 0)
      .filter((value) => Number.isFinite(value));
    const yValues = items
      .map((item) => item.table.layoutY ?? 0)
      .filter((value) => Number.isFinite(value));
    return {
      maxX: Math.max(...xValues, 100),
      maxY: Math.max(...yValues, 100),
    };
  }, [items]);

  if (access.status === 'loading' || seatingQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (access.status !== 'ready') {
    return <StatePanel preset="access-unavailable" />;
  }
  if (seatingQuery.isError) {
    return (
      <StatePanel
        preset="access-unavailable"
        detail={getGuestErrorMessage(seatingQuery.error, seatingErrorMessage)}
      />
    );
  }
  if (items.length === 0) {
    return (
      <StatePanel
        preset="empty"
        actions={
          <>
            <NavLink className="primary-button" to="/schedule">
              {`${msg('general.actions.open')} ${msg('general.modules.schedule.name')}`}{' '}
            </NavLink>
            <NavLink className="secondary-link" to="/">
              {msg('general.navigation.return_to_overview')}{' '}
            </NavLink>
          </>
        }
      />
    );
  }
  return (
    <section className="guest-page">
      <div className="guest-page__hero">
        <div>
          <p className="guest-page__eyebrow">
            {msg('general.modules.seating.name')}
          </p>
          <h2>
            {msg(
              'pages.seating_page.pages_seating_page_your_published_seating',
            )}
          </h2>
          <p className="guest-page__body">
            {msg(
              'pages.seating_page.pages_seating_page_only_seating_assignments_that_have_been_published_for',
            )}{' '}
          </p>
        </div>
        <div className="action-row">
          <NavLink className="secondary-link" to="/events">
            {msg('app.guest_workflows.visible_events')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/schedule">
            {msg('general.modules.schedule.name')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/help">
            {msg('pages.seating_page.pages_seating_page_seating_help')}{' '}
          </NavLink>
        </div>
      </div>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.seating_page.pages_seating_page_seating_context')}
        </p>
        <div className="detail-list">
          <article>
            <strong>{items.length}</strong>
            <span>{msg('pages.seating_page.published_assignments')}</span>
          </article>
          <article>
            <strong>
              {msg('pages.seating_page.pages_seating_page_companions_covered')}
            </strong>
            <span>
              {companionNames.join(', ') ||
                msg(
                  'pages.seating_page.pages_seating_page_no_linked_companions_published_yet',
                )}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.seating_page.pages_seating_page_accessibility_and_comfort',
              )}
            </strong>
            <span>
              {msg(
                'pages.seating_page.pages_seating_page_use_table_notes_and_assignment_notes_below_for',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.seating_page.pages_seating_page_event_specific_changes',
              )}
            </strong>
            <span>
              {msg(
                'pages.seating_page.pages_seating_page_if_a_seating_assignment_shifts_for_one_event',
              )}{' '}
            </span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.seating_page.pages_seating_page_table_map_view')}
        </p>
        <div className="guest-seating-map">
          {items.map((item) => {
            const left =
              ((item.table.layoutX ?? 0) / seatingMapLayout.maxX) * 100;
            const top =
              ((item.table.layoutY ?? 0) / seatingMapLayout.maxY) * 100;
            const accessibilityHint =
              (item.assignment.notes ?? item.table.notes ?? '')
                .toLowerCase()
                .includes('accessible') ||
              (item.assignment.notes ?? item.table.notes ?? '')
                .toLowerCase()
                .includes('wheelchair');
            return (
              <div
                className="guest-seating-map__marker"
                key={`seat-map:${item.assignment.id}`}
                style={
                  {
                    '--guest-seating-left': `${Math.min(left, 92)}%`,
                    '--guest-seating-top': `${Math.min(top, 82)}%`,
                  } as CSSProperties
                }
              >
                <strong>{item.table.label}</strong>
                <div>
                  {item.assignment.seatLabel || item.assignment.seatNumber
                    ? `${msg('pages.seating_page.seat')} ${
                        item.assignment.seatLabel ?? item.assignment.seatNumber
                      }`
                    : msg('pages.seating_page.pages_seating_page_seat_pending')}
                </div>
                {accessibilityHint ? (
                  <div>
                    {msg('pages.seating_page.pages_seating_page_accessible')}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="detail-list detail-list--stacked">
          {tableSummaries.map((table) => (
            <article key={`table-map:${table.id}`}>
              <strong>
                {table.title}
                {table.seatLabel
                  ? ` · ${msg('pages.seating_page.seat')} ${table.seatLabel}`
                  : ''}
              </strong>
              <span>{table.eventName}</span>
              <span>
                {msg('pages.seating_page.covered_companions_at_this_view')}{' '}
                {table.coveredCompanions.length > 0
                  ? table.coveredCompanions.join(', ')
                  : msg(
                      'pages.seating_page.pages_seating_page_no_linked_companions_published_yet',
                    )}
              </span>
              <span>{table.notes}</span>
            </article>
          ))}
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.seating_page.pages_seating_page_seating_alerts')}
        </p>
        {seatingAlerts.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {seatingAlerts.slice(0, 4).map((item) => (
              <article key={`seat-alert:${item.delivery.id}`}>
                <strong>
                  {item.message?.subjectText ??
                    msg('pages.seating_page.pages_seating_page_seating_update')}
                </strong>
                <span>
                  {item.message?.bodyText ??
                    msg(
                      'pages.seating_page.pages_seating_page_open_the_message_center_for_the_latest_seating_note',
                    )}
                </span>
                <NavLink
                  className="secondary-link"
                  to={item.deepLink ?? '/messages'}
                >
                  {msg(
                    'pages.seating_page.pages_seating_page_open_related_message',
                  )}{' '}
                </NavLink>
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.seating_page.pages_seating_page_no_recent_seating_specific_change_alerts_are_attached',
            )}{' '}
          </p>
        )}
      </TovCard>

      <div className="guest-card-list">
        {items.map((item) => (
          <article
            className="guest-card"
            key={`${item.event.id}:${item.assignment.id}`}
          >
            <h3>{item.event.displayName}</h3>
            <p>
              {item.table.label}
              {item.assignment.seatLabel || item.assignment.seatNumber
                ? ` · ${msg('pages.seating_page.seat')} ${
                    item.assignment.seatLabel ?? item.assignment.seatNumber
                  }`
                : ''}
            </p>
            <p>{item.seatingPlan.displayName}</p>
            <p>
              {item.table.notes ??
                item.assignment.notes ??
                msg(
                  'pages.seating_page.pages_seating_page_no_extra_seating_notes',
                )}
            </p>
            <div className="detail-list detail-list--stacked">
              <article>
                <strong>
                  {msg('pages.seating_page.pages_seating_page_table_context')}
                </strong>
                <span>
                  {item.table.label}{' '}
                  {msg('pages.seating_page.pages_seating_page_for')}{' '}
                  {item.event.displayName}
                  {msg(
                    'pages.seating_page.pages_seating_page_review_event_timing_before_heading_to_the_seating',
                  )}{' '}
                </span>
              </article>
              <article>
                <strong>
                  {msg('pages.access_page.pages_access_page_people_covered')}
                </strong>
                <span>
                  {companionNames.join(', ') ||
                    msg(
                      'pages.seating_page.pages_seating_page_guest_unit_details_pending',
                    )}
                </span>
              </article>
              <article>
                <strong>
                  {msg(
                    'pages.seating_page.pages_seating_page_accommodation_notes',
                  )}
                </strong>
                <span>
                  {item.assignment.notes ??
                    item.table.notes ??
                    msg(
                      'pages.seating_page.pages_seating_page_no_published_accessibility_or_comfort_notes_for_this_assignment',
                    )}
                </span>
              </article>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
